import { useState } from 'react';
import { motion } from 'framer-motion';
import { CounterUp } from './CounterUp';
import { Calculator, IndianRupee, TrendingUp, Clock } from 'lucide-react';

/* ═══════════════════════════════════════════
   EDITABLE: Calculator section stats
═══════════════════════════════════════════ */
const stats = [
  { label: 'Years of Experience', value: 30, suffix: '+', icon: Clock },
  { label: 'Happy Clients', value: 5000, suffix: '+', icon: TrendingUp },
  { label: 'AUM', value: 100, suffix: ' Cr+', icon: IndianRupee },
];

export const CalculatorSection = () => {
  const [monthly, setMonthly] = useState(10000);
  const [rate, setRate] = useState(12);
  const [years, setYears] = useState(10);

  const totalInvested = monthly * 12 * years;
  const r = rate / 100 / 12;
  const n = years * 12;
  const futureValue = monthly * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
  const estimatedReturns = futureValue - totalInvested;

  const formatCurrency = (num) => {
    if (num >= 10000000) return `${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `${(num / 100000).toFixed(2)} L`;
    return `${num.toLocaleString('en-IN')}`;
  };

  // Donut chart percentage
  const investedPercent = (totalInvested / futureValue) * 100;
  const returnsPercent = 100 - investedPercent;

  return (
    <section data-testid="calculator-section" className="py-20 md:py-32 bg-[#0A192F] relative overflow-hidden">
      {/* Subtle geometric background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: `radial-gradient(circle at 20% 80%, #D4AF37 1px, transparent 1px), radial-gradient(circle at 80% 20%, #D4AF37 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#D4AF37] mb-4">
            Wealth Calculator
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-white mb-4">
            The Power of Compounding
          </h2>
          <p className="text-base text-white/50 max-w-2xl mx-auto font-['Outfit']">
            Make your money work smarter with the power of compounding. Use this interactive calculator to explore how consistent investments grow significantly over time.
          </p>
        </motion.div>

        {/* Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-3 gap-4 md:gap-8 mb-16 max-w-3xl mx-auto"
        >
          {stats.map((stat, index) => (
            <div key={stat.label} data-testid={`stat-${index}`} className="text-center">
              <div className="w-12 h-12 mx-auto bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-3">
                <stat.icon size={20} className="text-[#D4AF37]" />
              </div>
              <p className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-white">
                <CounterUp end={stat.value} suffix={stat.suffix} />
              </p>
              <p className="text-xs sm:text-sm text-white/50 mt-1 font-['Outfit']">{stat.label}</p>
            </div>
          ))}
        </motion.div>

        {/* Calculator Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-[#112240] rounded-2xl border border-white/5 overflow-hidden shadow-2xl"
        >
          <div className="grid grid-cols-1 lg:grid-cols-5">
            {/* Controls - Left */}
            <div className="lg:col-span-3 p-8 md:p-10 border-b lg:border-b-0 lg:border-r border-white/5">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                  <Calculator size={20} className="text-[#0A192F]" />
                </div>
                <h3 className="font-['Playfair_Display'] text-lg font-bold text-white">SIP Calculator</h3>
              </div>

              {/* Monthly Investment */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm text-white/60 font-['Outfit']">Monthly Investment</label>
                  <div className="bg-white/5 rounded-lg px-3 py-1.5 border border-white/10">
                    <span className="text-sm font-semibold text-[#D4AF37] font-['Outfit']" data-testid="calc-monthly-value">{monthly.toLocaleString('en-IN')}</span>
                  </div>
                </div>
                <input
                  data-testid="calc-monthly-input"
                  type="range"
                  min="1000"
                  max="200000"
                  step="1000"
                  value={monthly}
                  onChange={(e) => setMonthly(Number(e.target.value))}
                  className="w-full h-1.5 rounded-full appearance-none bg-white/10 accent-[#D4AF37] cursor-pointer"
                />
                <div className="flex justify-between text-xs text-white/30 mt-1 font-['Outfit']">
                  <span>1K</span><span>2L</span>
                </div>
              </div>

              {/* Expected Return */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm text-white/60 font-['Outfit']">Expected Return (p.a.)</label>
                  <div className="bg-white/5 rounded-lg px-3 py-1.5 border border-white/10">
                    <span className="text-sm font-semibold text-[#D4AF37] font-['Outfit']" data-testid="calc-rate-value">{rate}%</span>
                  </div>
                </div>
                <input
                  data-testid="calc-rate-input"
                  type="range"
                  min="1"
                  max="30"
                  step="0.5"
                  value={rate}
                  onChange={(e) => setRate(Number(e.target.value))}
                  className="w-full h-1.5 rounded-full appearance-none bg-white/10 accent-[#D4AF37] cursor-pointer"
                />
                <div className="flex justify-between text-xs text-white/30 mt-1 font-['Outfit']">
                  <span>1%</span><span>30%</span>
                </div>
              </div>

              {/* Time Period */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm text-white/60 font-['Outfit']">Time Period</label>
                  <div className="bg-white/5 rounded-lg px-3 py-1.5 border border-white/10">
                    <span className="text-sm font-semibold text-[#D4AF37] font-['Outfit']" data-testid="calc-years-value">{years} yrs</span>
                  </div>
                </div>
                <input
                  data-testid="calc-years-input"
                  type="range"
                  min="1"
                  max="40"
                  step="1"
                  value={years}
                  onChange={(e) => setYears(Number(e.target.value))}
                  className="w-full h-1.5 rounded-full appearance-none bg-white/10 accent-[#D4AF37] cursor-pointer"
                />
                <div className="flex justify-between text-xs text-white/30 mt-1 font-['Outfit']">
                  <span>1 yr</span><span>40 yrs</span>
                </div>
              </div>
            </div>

            {/* Results - Right */}
            <div className="lg:col-span-2 p-8 md:p-10 flex flex-col items-center justify-center bg-[#0A192F]/50">
              {/* Visual Donut */}
              <div className="relative w-40 h-40 mb-8">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#1a3355" strokeWidth="12" />
                  <circle
                    cx="50" cy="50" r="40" fill="none"
                    stroke="#D4AF37" strokeWidth="12"
                    strokeDasharray={`${investedPercent * 2.51} ${251}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <p className="text-lg font-bold text-white font-['Playfair_Display']" data-testid="calc-total">{formatCurrency(Math.round(futureValue))}</p>
                  <p className="text-[10px] text-white/50 font-['Outfit']">Total Value</p>
                </div>
              </div>

              {/* Breakdown */}
              <div className="w-full space-y-4">
                <div className="flex items-center justify-between bg-white/5 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-[#D4AF37] rounded-full" />
                    <span className="text-xs text-white/60 font-['Outfit']">Invested</span>
                  </div>
                  <span className="text-sm font-bold text-white font-['Outfit']" data-testid="calc-invested">{formatCurrency(totalInvested)}</span>
                </div>
                <div className="flex items-center justify-between bg-white/5 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-[#1a3355] rounded-full border border-[#D4AF37]/30" />
                    <span className="text-xs text-white/60 font-['Outfit']">Est. Returns</span>
                  </div>
                  <span className="text-sm font-bold text-[#D4AF37] font-['Outfit']" data-testid="calc-returns">{formatCurrency(Math.round(estimatedReturns))}</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
